import { Component, Inject, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { forkJoin, of, Subject } from 'rxjs';
import { takeUntil, take, switchMap, catchError } from 'rxjs/operators';
import { State } from 'src/app/store';
import { accessSelector } from 'src/app/global/store/global.selectors';
import { GetAccessAction, BatchAssignRoleAction, BatchAssignRoleSuccessAction, BatchAssignRoleErrorAction } from 'src/app/global/store/global.actions';
import { AccessControl, Role, UserRoleResponce, AssignPayload } from 'src/app/global/services/access-control/access-control.model';
import { AccessControlService } from 'src/app/global/services/access-control/access-control.abstract.service';
import { MessageService } from 'src/app/global/services/message/message.abstract.service';
import { TranslateService } from '@ngx-translate/core';

export interface AccessControlRoleDialogData {
    title?: string;
    mode?: 'assign' | 'manage'; // assign users to roles or manage role assignments
    role?: AccessControl; // Specific role for direct assignment
}

@Component({
    selector: 'Access-control-assign',
    templateUrl: './access-control-role-dialog.component.html',
    styleUrls: ['./access-control-role-dialog.component.scss'],
})
export class AccessControlRoleDialogComponent implements OnInit, OnDestroy {
    public loading = false;

    public destroy$ = new Subject<void>();

    // Role management
    availableRoles: AccessControl[] = [];

    selectedRole: AccessControl | null = null;

    // User management
    selectedUsers: any[] = [];

    assignedUsers: any[] = [];

    public showNoUsersFound = false;

    public assignableRoles: Role[] = [];

    @Output() onSubmit: EventEmitter<any> = new EventEmitter<any>();

    constructor(
        private store: Store<State>,
        private actions$: Actions,
        private accessService: AccessControlService,
        private messageService: MessageService,
        private translate: TranslateService,
        public dialogRef: MatDialogRef<AccessControlRoleDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: AccessControlRoleDialogData,
    ) {}

    ngOnInit(): void {
        // If a specific role is provided, use it directly
        if (this.data?.role) {
            this.selectedRole = this.data.role;
            this.assignableRoles = [
                {
                    name: this.data.role.title,
                    id: this.data.role._id,
                    userId: '',
                    selected: true, // Pre-select the provided role
                },
            ];
            this.loadUsersForRole(this.data.role._id!);
        } else {
            this.loadRoles();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private loadRoles(): void {
        // Load available roles from store
        this.store
            .select(accessSelector)
            .pipe(takeUntil(this.destroy$))
            .subscribe((roles) => {
                this.availableRoles = roles;
                roles.forEach((role) => {
                    this.assignableRoles.push({
                        name: role.title,
                        id: role?._id,
                        userId: '',
                        selected: false,
                    });
                });
                if (roles.length === 0) {
                    // If no roles in store, dispatch action to load them
                    this.store.dispatch(GetAccessAction());
                }
            });
    }

    onRoleSelected(role: AccessControl): void {
        this.selectedRole = role;
        this.loadUsersForRole(role._id!);
    }

    private loadUsersForRole(roleId: string): void {
        this.loading = true;
        this.accessService
            .getRoleUsers(roleId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: UserRoleResponce) => {
                    this.assignedUsers = response.data || [];
                    this.loading = false;
                },
                error: (error) => {
                    console.error('Error loading users for role:', error);
                    this.assignedUsers = [];
                    this.loading = false;
                },
            });
    }

    onUsersSelected(users: any[]): void {
        this.selectedUsers = users;
    }

    onNoUsersFound(noUsersFound: boolean): void {
        this.showNoUsersFound = noUsersFound;
    }

    roleIsSelected(): boolean {
        return this.assignableRoles.some((role) => role.selected);
    }

    // Add this method to handle role selection changes from the child
    onRoleSelectionChanged(selectedRoles: Role[]): void {
        // Update assignableRoles' selected state based on the selectedRoles array
        this.assignableRoles.forEach((role) => {
            role.selected = selectedRoles.some((selected) => selected.id === role.id);
        });
    }

    assignUsers(): void {
        if (this.selectedUsers.length > 0 && this.roleIsSelected()) {
            this.loading = true;

            const assignments: AssignPayload[] = [];
            this.assignableRoles.forEach((role) => {
                if (role.selected === true) {
                    this.selectedUsers.forEach((user) => {
                        assignments.push({
                            roleId: role.id,
                            userId: user.id,
                        });
                    });
                }
            });

            this.store.dispatch(BatchAssignRoleAction({ assignments }));

            // Listen for success action
            this.actions$
                .pipe(
                    ofType(BatchAssignRoleSuccessAction),
                    take(1),
                    takeUntil(this.destroy$),
                    // After successful assignment, refresh the role users
                    switchMap(() => {
                        const selectedRoles = this.assignableRoles.filter((role) => role.selected === true);

                        if (selectedRoles.length === 0) {
                            return of([]);
                        }

                        const roleUserRequests = selectedRoles.map((role) =>
                            this.accessService.getRoleUsers(role.id).pipe(
                                take(1), // Ensure the observable completes after first emission
                                catchError((error) => {
                                    console.error('Error loading users for role:', error);
                                    return of(null);
                                }),
                            ),
                        );

                        return forkJoin(roleUserRequests);
                    }),
                )
                .subscribe({
                    next: () => {
                        // IMPORTANT: Refresh the access control data to update the table
                        this.store.dispatch(GetAccessAction());

                        this.messageService.success(this.translate.instant('access_control.dialog.success'));

                        // Reset form
                        this.selectedUsers = [];
                        this.loading = false;
                        this.close();
                    },
                    error: (error) => {
                        console.error('Error refreshing role users after assignment:', error);
                        this.loading = false;
                    },
                });

            // Listen for error action
            this.actions$.pipe(ofType(BatchAssignRoleErrorAction), take(1), takeUntil(this.destroy$)).subscribe({
                next: (action) => {
                    this.loading = false;
                    if (action.error?.error?.messages?.[0] === 'access.errors.assignDuplicateRole') {
                        this.close();
                    } else {
                        this.messageService.error(this.translate.instant('access_control.dialog.error'));
                    }
                },
                error: (error) => {
                    console.error('Error assigning users to roles:', error);
                    this.messageService.error(this.translate.instant('access_control.dialog.error'));
                    this.loading = false;
                },
            });
        }
    }

    close(): void {
        this.dialogRef.close();
    }

    cancel(): void {
        this.dialogRef.close();
    }

    getRoleDisplayText(role: AccessControl): string {
        return typeof role.title === 'string' ? role.title : role.title[this.translate.currentLang] || role.title.en || role.title.fr || 'Unknown Role';
    }

    getRoleDescriptionText(role: AccessControl): string {
        return typeof role.description === 'string' ? role.description : role.description[this.translate.currentLang] || role.description.en || role.description.fr || 'Unknown Role';
    }

    getUserAvatarUrl(user: any): string {
        return user?.picture || 'https://www.pphfoundation.ca/wp-content/uploads/2018/05/default-avatar.png';
    }

    trackByUserId(index: number, user: any): any {
        return user.id || user._id || index;
    }

    removeSelectedUser(userToRemove: any): void {
        this.selectedUsers = this.selectedUsers.filter((user) => (user.id || user._id) !== (userToRemove.id || userToRemove._id));
    }
}
